/**
 * The Google Workspace onboarding panel on the integrations page.
 *
 * Three states, in the order the technician meets them:
 *
 * 1. **Signing in** -- a live browser (the provider's own view, loaded directly) on the admin
 *    console, then on [Next] the Cloud console, then [I'm done]. Both buttons are enabled at once;
 *    the technician judges when they are signed in (Q91).
 * 2. **Running** -- the browser is replaced by the nine-row checklist, polled every three seconds.
 *    The row in flight says "Running..."; a row whose attempt failed but has attempts left is
 *    amber and says it is trying again; a row that had a browser carries a view-recording icon
 *    (Q66b, Q89); the one step with no browser says so (Q101).
 * 3. **Finished** -- completed, or failed. A failure is final for the run (Q96, Q117): the failing
 *    row is red with its error and every row after it is red because of it; the page shows the
 *    technician what to do by hand. Once they have done it and saved after a green Test, the run
 *    carries a stamp and those rows turn green as "completed manually" (Q99, Q126).
 *
 * The page owns the buttons. It learns what the run is through ``onRunChange`` and decides
 * from that what [Start], [Save] and the JSON field do (Q132).
 *
 * The iframe is held at the session's native 1280x720 and scaled to fit, for the same reason the
 * chat panel does it: the live view does not reflow when its viewport changes.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ReplayIcon from "@mui/icons-material/Replay";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";

import GWorkspaceOnboardingApi, { OnboardingRun, OnboardingStep, SignInSession } from "../../apis/gworkspaceOnboardingApi";

const api = new GWorkspaceOnboardingApi();

export const POLL_MS = 3000;

const LIVE_NATIVE_WIDTH = 1280;
const LIVE_NATIVE_HEIGHT = 720;
const LIVE_EXPANDED_Z = 1300;

const frameBase = { borderRadius: 2, overflow: "hidden", border: "1px solid #E2E8F0", background: "#FFFFFF" };
const inlineFrameSx = { ...frameBase, position: "relative" as const, width: "100%", maxWidth: 960, aspectRatio: "16 / 9" };
const expandedFrameSx = {
  ...frameBase,
  position: "fixed" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: LIVE_EXPANDED_Z + 1,
  width: "min(1400px, 94vw, calc(88vh * 16 / 9))",
  aspectRatio: "16 / 9",
  boxShadow: "0 24px 64px rgba(9, 14, 26, 0.45)",
};

export const EXPLAINER =
  "Before we proceed with onboarding for Google, we need you to sign in to the Google Admin console and the Google Cloud console. Please sign in, in the browser session below.";

export const NO_RECORDING_NOTE = "No recording, this step calls Google's API directly";
/** The tail on a refused address. What to change is the step's to say; this is how to resume. */
export const RETRY_NOTE = "Press Start again.";
/** Only a fallback: the run carries the sentence the step raised, which says the same thing. */
export const NOT_AN_ADMIN_NOTE =
  "The Google account signed in to the browser is not an administrator of this tenant. Press Start again and sign in as a Super Admin.";
export const CASCADE_NOTE = "Failed because an earlier step failed";
export const RETRYING_NOTE = "That attempt did not work. Trying again...";
export const STARTING_NOTE = "Starting...";
export const MANUAL_NOTE = "Completed manually by the technician";
/** The server's word for the flag being off, said the way a technician can act on. */
export const BROWSER_USE_DISABLED_NOTE =
  "Browser automation is not switched on for this customer, so Theo cannot open the sign-in. Ask Theo support to switch it on, then press Start.";

export interface GWorkspaceOnboardingPanelProps {
  customerId: string | number;
  applicationId: string | number;
  /** The admin account the run will create (Q49). Read at [I'm done]. */
  adminEmail: string;
  /** The modal's checkbox: give a created admin a Workspace licence (Q145, Q161). Read at [I'm done]; on when absent. */
  assignLicense?: boolean;
  /** Bumped by the page's [Start] button; each new value opens a sign-in. */
  startSignal: number;
  /** The newest run for this customer, every time the panel reads it (including on open). */
  onRunChange?: (run: OnboardingRun | null) => void;
  /** The run reached a terminal state while the panel was watching it. */
  onFinished?: (run: OnboardingRun) => void;
  /** The service-account JSON has landed in application_config. */
  onConfigurationSaved?: (run: OnboardingRun) => void;
  /** Sign-in cancelled before a job was started. */
  onCancelled?: () => void;
  /** The sign-in could not be opened at all, so [Start] has to come back (Q163). */
  onStartFailed?: () => void;
  /**
   * A green connection test on a failed run: the technician has finished the steps by hand.
   *
   * The durable record is the manually_completed_at stamp that [Save] writes, but the test is
   * the evidence and it passes first. Without this the rows stay red underneath a green report
   * until the technician saves, reopens, and finds out it had worked (Q119, Q125, Q126).
   */
  manuallyVerified?: boolean;
}

type Phase = "idle" | "opening" | "admin" | "cloud" | "running" | "finished";

/** ``retrying`` and ``starting`` are display states only: the server has neither (see displayRows). */
export type DisplayState = OnboardingStep["state"] | "retrying" | "starting";

const STEP_ICON: Record<DisplayState, React.ReactNode> = {
  pending: <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "#B4BCD0" }} />,
  running: <CircularProgress size={16} sx={{ color: "#3B82F6" }} />,
  starting: <CircularProgress size={14} sx={{ color: "#8A93AB" }} />,
  retrying: <ReplayIcon sx={{ fontSize: 18, color: "#D97706" }} />,
  done: <CheckCircleIcon sx={{ fontSize: 18, color: "#22C55E" }} />,
  failed: <CancelIcon sx={{ fontSize: 18, color: "#EF4444" }} />,
};

const STEP_COLOR: Record<DisplayState, string> = {
  pending: "#8A93AB",
  running: "#1F2937",
  starting: "#5B6583",
  retrying: "#B45309",
  done: "#1F2937",
  failed: "#B91C1C",
};

export function isTerminal(run: OnboardingRun | null | undefined): boolean {
  return Boolean(run && (run.status === "completed" || run.status === "failed"));
}

/**
 * A failed run the technician can simply start again. Two reasons qualify, and what they have
 * in common is that the fix is on this screen rather than in the consoles:
 *
 * - ``bad_admin_email`` (Q162) -- the typed address was taken by an account that is not a Super
 *   Admin, or is suspended, or is on another domain. Another address is the fix.
 * - ``not_an_admin`` (Q163) -- the console refused the account the browser was signed in as.
 *   Theo has signed that account out, so pressing Start opens a chooser; signing in as somebody
 *   who can administer the tenant is the fix.
 *
 * Neither leaves anything to finish by hand, so [Start] stays instead of [Save].
 */
export const RECOVERABLE_REASONS = ["bad_admin_email", "not_an_admin"];

export function isRecoverable(run: OnboardingRun | null | undefined): boolean {
  return Boolean(run && run.status === "failed" && RECOVERABLE_REASONS.includes(run.failure?.reason ?? ""));
}

export type DisplayRow = Omit<OnboardingStep, "state"> & { state: DisplayState; note: string | null };

/**
 * The rows as the checklist shows them, from the run as the server reports it. Pure.
 *
 * A failed run reddens every step after the one that failed (Q96); a manually completed one
 * greens every step the run did not do (Q99, Q119). Neither invents an error message. A
 * recoverable failure reddens only the step that refused the address: the rest were never
 * reached and are about to be run again, so they stay pending.
 *
 * A step that failed while the run is still going has attempts left -- the worker put its row
 * back in the queue and will claim it again after a backoff of 30s and up. Red would say the
 * opposite of what is happening, so it is amber and says it is trying again. Red is kept for
 * what red means: the run is over and this step is why.
 *
 * Between one step finishing and the worker claiming the next, no row says anything: the step
 * just done is green and the next is still pending, which reads as stuck until "Running..."
 * appears out of nowhere. So the next step in line says it is starting.
 */
export function displayRows(run: OnboardingRun, manuallyVerified = false): DisplayRow[] {
  const manual = Boolean(run.manually_completed_at) || (manuallyVerified && run.status === "failed");
  const cascade = run.status === "failed" && !isRecoverable(run);
  const failedAt = cascade ? run.steps.findIndex((step) => step.state === "failed") : -1;
  // Only when nothing else is in flight: a step that is running, or one waiting on its retry
  // backoff, is already the answer to "what happens next". The run's own "Starting..." covers
  // the gap before the first step, so the rows do not repeat it.
  const startingAt =
    run.status === "running" && !isStarting(run) && run.steps.every((step) => step.state !== "running" && step.state !== "failed")
      ? run.steps.findIndex((step) => step.state === "pending")
      : -1;
  return run.steps.map((step, index) => {
    if (manual && step.state !== "done") return { ...step, state: "done", error: null, note: MANUAL_NOTE };
    if (failedAt >= 0 && index > failedAt && step.state !== "done") return { ...step, state: "failed", error: null, note: CASCADE_NOTE };
    if (run.status === "running" && step.state === "failed") return { ...step, state: "retrying", note: RETRYING_NOTE };
    if (index === startingAt) return { ...step, state: "starting", note: null };
    return { ...step, note: step.note ?? null };
  });
}

/**
 * What the technician is told when a step fails.
 *
 * Not the error the step raised: that is a class name and a selector, written for whoever
 * wrote the script, and there is nothing in it a technician can act on. The step number is
 * enough to find it -- the row above is red and carries the title -- and what to do about it
 * is directly below. The raw error stays in the ledger and the logs for whoever wants it.
 *
 * The one exception is a refused address: that error is written for the technician, names the
 * address and says what is wrong with it, and the fix is to type another and press Start.
 */
export function failureMessage(rows: DisplayRow[], run?: OnboardingRun | null): string | null {
  const failed = rows.findIndex((step) => step.state === "failed");
  if (failed < 0) return null;
  const reason = run?.failure?.reason ?? "";
  const said = rows[failed].error?.trim() || run?.failure?.message?.trim() || "";
  // A refused account already says what is wrong and what to do; the address is not the problem,
  // so it must not be told to type a different one.
  if (reason === "not_an_admin") return said || NOT_AN_ADMIN_NOTE;
  if (isRecoverable(run)) return `${said ? `${said} ` : ""}Nothing was created. ${RETRY_NOTE}`;
  return `Theo could not complete step ${failed + 1}. Finish the steps below by hand, then press Test.`;
}

/** Between [I'm done] and the worker claiming the first job nothing has moved yet (Q106). */
export function isStarting(run: OnboardingRun): boolean {
  return run.status === "running" && run.steps.every((step) => step.state === "pending");
}

export default function GWorkspaceOnboardingPanel(props: GWorkspaceOnboardingPanelProps) {
  const {
    customerId,
    applicationId,
    adminEmail,
    assignLicense,
    startSignal,
    manuallyVerified,
    onRunChange,
    onFinished,
    onConfigurationSaved,
    onCancelled,
    onStartFailed,
  } = props;

  const [phase, setPhase] = useState<Phase>("idle");
  const [session, setSession] = useState<SignInSession | null>(null);
  const [run, setRun] = useState<OnboardingRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [recording, setRecording] = useState<{ step: DisplayRow; url: string | null; error: string | null } | null>(null);

  const seenSignal = useRef(startSignal);
  // Keyed by run, not job: every step of a run is its own job.
  const savedNotified = useRef<number | null>(null);
  const finishedNotified = useRef<number | null>(null);
  const loggedSession = useRef<string | null>(null);

  const fail = (fallback: string) => (err: unknown) => {
    const message = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || (err as Error)?.message || fallback;
    // The flag comes back as its own key, which is a sentence to nobody.
    setError(String(message) === "browser_use_disabled" ? BROWSER_USE_DISABLED_NOTE : String(message));
  };

  const applyRun = useCallback(
    (next: OnboardingRun | null) => {
      setRun(next);
      onRunChange?.(next);
      if (!next) return;
      if (next.configuration_saved && savedNotified.current !== next.run_id) {
        savedNotified.current = next.run_id;
        onConfigurationSaved?.(next);
      }
      if (isTerminal(next)) {
        setPhase("finished");
        if (finishedNotified.current !== next.run_id) {
          finishedNotified.current = next.run_id;
          onFinished?.(next);
        }
      } else {
        setPhase("running");
      }
    },
    [onRunChange, onConfigurationSaved, onFinished]
  );

  // A run already going, or a finished one, is shown as soon as the page opens (Q55, Q62, Q94).
  useEffect(() => {
    let cancelled = false;
    api
      .getStatus(customerId, applicationId)
      .then(({ run: existing }) => {
        if (cancelled) return;
        // A finished run from an earlier visit is history: shown, read-only, but not reported as
        // having just finished -- the page runs nothing on a visit (Q104). A live one is watched.
        if (existing && !isTerminal(existing)) {
          applyRun(existing);
        } else {
          if (existing) finishedNotified.current = existing.run_id;
          setRun(existing);
          onRunChange?.(existing);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, applicationId]);

  // Polling while the run is live.
  useEffect(() => {
    if (phase !== "running") return undefined;
    let stopped = false;
    const tick = () =>
      api
        .getStatus(customerId, applicationId)
        .then(({ run: latest }) => {
          if (!stopped && latest) applyRun(latest);
        })
        .catch(() => undefined);
    const timer = setInterval(tick, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [phase, customerId, applicationId, applyRun]);

  // The session id is for whoever is debugging, not for the technician (Q105): the log, not the page.
  useEffect(() => {
    const current = run?.steps.find((step) => step.state === "running");
    if (!current?.session_id || loggedSession.current === current.session_id) return;
    loggedSession.current = current.session_id;
    console.log("[gworkspace-onboarding] step %s running in session %s (job %s)", current.name, current.session_id, run?.job_id);
  }, [run]);

  const openSignIn = useCallback(() => {
    setError(null);
    // Starting again after a refused address (Q162): the old run's checklist would otherwise sit
    // under the sign-in until the first poll replaces it.
    setRun(null);
    setPhase("opening");
    setBusy(true);
    api
      .startSignIn(customerId, applicationId)
      .then((opened) => {
        setSession(opened);
        setPhase("admin");
      })
      .catch((err) => {
        fail("Could not open a browser for the sign-in")(err);
        setPhase("idle");
        // Nothing was started, so the page has to give [Start] back; otherwise the button greys
        // itself out over an error the panel used to render nothing for.
        onStartFailed?.();
      })
      .finally(() => setBusy(false));
  }, [customerId, applicationId, onStartFailed]);

  // The page's [Start] button.
  useEffect(() => {
    if (startSignal === seenSignal.current) return;
    seenSignal.current = startSignal;
    openSignIn();
  }, [startSignal, openSignIn]);

  const onNext = () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    api
      .nextSignIn(customerId, applicationId, session.session_id)
      .then(() => setPhase("cloud"))
      .catch(fail("Could not open the Cloud console"))
      .finally(() => setBusy(false));
  };

  const onDone = () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    api
      .finishSignIn(customerId, applicationId, session.session_id, adminEmail, assignLicense ?? true)
      .then(() => {
        setSession(null);
        setPhase("running");
        return api.getStatus(customerId, applicationId).then(({ run: started }) => started && applyRun(started));
      })
      .catch(fail("Could not start the setup"))
      .finally(() => setBusy(false));
  };

  const onCancel = () => {
    if (!session) {
      setPhase("idle");
      onCancelled?.();
      return;
    }
    setBusy(true);
    api
      .cancelSignIn(customerId, applicationId, session.session_id)
      .catch(() => undefined)
      .finally(() => {
        setBusy(false);
        setSession(null);
        setPhase("idle");
        onCancelled?.();
      });
  };

  const viewRecording = (step: DisplayRow) => {
    if (!step.session_id) return;
    setRecording({ step, url: null, error: null });
    api
      .getRecordingUrl(customerId, applicationId, step.session_id)
      .then(({ url }) => setRecording((open) => (open && open.step === step ? { ...open, url } : open)))
      .catch(() => setRecording((open) => (open && open.step === step ? { ...open, error: "The recording is not available." } : open)));
  };

  // Scale the fixed-size iframe to its container.
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const measure = () => {
      const width = frame.clientWidth;
      if (width > 0) setScale(width / LIVE_NATIVE_WIDTH);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [session, expanded]);

  const rows = useMemo(() => (run ? displayRows(run, manuallyVerified) : []), [run, manuallyVerified]);
  const manual = Boolean(run && (run.manually_completed_at || (manuallyVerified && run.status === "failed")));
  const starting = Boolean(run && isStarting(run));
  const recoverable = isRecoverable(run);
  const failureNote = run?.status === "failed" && !manual ? failureMessage(rows, run) : null;

  const signingIn = phase === "opening" || phase === "admin" || phase === "cloud";

  // An error with nothing else to show still has to show: a failed sign-in leaves no run behind,
  // and returning null here is what made [Start] look like it did nothing at all.
  if (phase === "idle" && !run && !error) return null;

  const heading = !run
    ? ""
    : run.status === "completed"
      ? "Setup complete"
      : run.status === "failed"
        ? manual
          ? "Setup completed manually"
          : // "did not start" only when nothing did: a refused account can be refused at step 7,
            // by which point six steps have run and saying otherwise would be a lie.
            recoverable && rows.every((row) => row.state !== "done")
            ? "Setup did not start"
            : "Setup stopped"
        : `Setting up Google Workspace${run.admin_email ? ` as ${run.admin_email}` : ""}...`;

  const slotFor = (step: DisplayRow): React.ReactNode => {
    if (step.state === "retrying") {
      return (
        <Typography data-testid={`onboarding-step-${step.name}-retrying`} sx={{ fontSize: 12, color: "#B45309", fontWeight: 600 }}>
          {step.note}
        </Typography>
      );
    }
    if (step.state === "starting") {
      return (
        <Typography data-testid={`onboarding-step-${step.name}-starting`} sx={{ fontSize: 12, color: "#8A93AB" }}>
          {STARTING_NOTE}
        </Typography>
      );
    }
    // Capped and right-aligned: a long note used to take the row's width and squeeze the title
    // into a four-line column of its own.
    if (step.note) return <Typography sx={{ fontSize: 12, color: "#5B6583", maxWidth: "58%", textAlign: "right" }}>{step.note}</Typography>;
    if (step.state === "running") {
      return (
        <Typography data-testid={`onboarding-step-${step.name}-running`} sx={{ fontSize: 12, color: "#3B82F6", fontWeight: 600 }}>
          Running...
        </Typography>
      );
    }
    if (step.console === "api" && step.state !== "pending") {
      return <Typography sx={{ fontSize: 12, color: "#5B6583" }}>{NO_RECORDING_NOTE}</Typography>;
    }
    if (step.has_recording && step.session_id) {
      return (
        <Tooltip title="View the recording of this step">
          <IconButton size="small" aria-label={`View the recording of ${step.title}`} onClick={() => viewRecording(step)}>
            <PlayCircleOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <Box data-testid="gworkspace-onboarding-panel" sx={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
      {error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {signingIn ? (
        <>
          <Alert severity="info" data-testid="gworkspace-onboarding-explainer" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{EXPLAINER}</Typography>
            <Typography sx={{ fontSize: 13, mt: 0.5 }}>
              {phase === "cloud"
                ? "Step 2 of 2 — sign in to the Google Cloud console, then press I'm done."
                : "Step 1 of 2 — sign in to the Google Admin console, then press Next."}
            </Typography>
          </Alert>

          <Box onClick={() => setExpanded(false)} sx={{ display: expanded ? "block" : "none", position: "fixed", inset: 0, bgcolor: "rgba(9, 14, 26, 0.62)", zIndex: LIVE_EXPANDED_Z }} />
          <Box ref={frameRef} sx={expanded ? expandedFrameSx : inlineFrameSx}>
            {session ? (
              <Box
                component="iframe"
                title="Google sign-in"
                data-testid="gworkspace-onboarding-live"
                src={session.live_url}
                sx={{
                  display: "block",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: LIVE_NATIVE_WIDTH,
                  height: LIVE_NATIVE_HEIGHT,
                  border: "none",
                  transformOrigin: "top left",
                  transform: `scale(${scale})`,
                }}
              />
            ) : (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, background: "#FBFCFF" }}>
                <CircularProgress size={16} sx={{ color: "#8A93AB" }} />
                <Typography sx={{ fontSize: 12, color: "#5B6583" }}>Opening the browser...</Typography>
              </Box>
            )}
            <Tooltip title={expanded ? "Shrink the browser" : "Open the browser larger"}>
              <IconButton
                onClick={() => setExpanded((open) => !open)}
                aria-label={expanded ? "Shrink the browser view" : "Open the browser in a larger view"}
                size="small"
                sx={{ position: "absolute", top: 6, right: 6, zIndex: 1, bgcolor: "rgba(15, 23, 42, 0.6)", color: "#FFFFFF", "&:hover": { bgcolor: "rgba(15, 23, 42, 0.85)" } }}
              >
                {expanded ? <CloseFullscreenIcon sx={{ fontSize: 16 }} /> : <OpenInFullIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button variant="outlined" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            {phase === "cloud" ? (
              <Button variant="contained" onClick={onDone} disabled={busy || !session}>
                I'm done
              </Button>
            ) : (
              <Button variant="contained" onClick={onNext} disabled={busy || !session}>
                Next
              </Button>
            )}
          </Box>
        </>
      ) : null}

      {run && !signingIn ? (
        <Box data-testid="gworkspace-onboarding-checklist" sx={{ border: "1px solid #E2E8F0", borderRadius: "8px", p: 2, background: "#FFF" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1F2937", mb: starting ? 0.5 : 1 }}>{heading}</Typography>
          {starting ? (
            <Box data-testid="gworkspace-onboarding-starting" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CircularProgress size={12} sx={{ color: "#8A93AB" }} />
              <Typography sx={{ fontSize: 12, color: "#5B6583" }}>Starting...</Typography>
            </Box>
          ) : null}
          <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
            {rows.map((step, index) => (
              <Box component="li" key={step.name} data-testid={`onboarding-step-${step.name}`} data-state={step.state} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 20, display: "flex", justifyContent: "center" }}>{STEP_ICON[step.state]}</Box>
                <Typography sx={{ fontSize: 13, color: STEP_COLOR[step.state], flex: 1 }}>
                  {index + 1}. {step.title}
                  {step.attempts > 1 && step.state !== "done" ? ` (attempt ${step.attempts})` : ""}
                </Typography>
                {slotFor(step)}
              </Box>
            ))}
          </Box>
          {failureNote ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {failureNote}
            </Alert>
          ) : null}
        </Box>
      ) : null}

      <Dialog open={Boolean(recording)} onClose={() => setRecording(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 15 }}>{recording?.step.title}</DialogTitle>
        <DialogContent>
          {recording?.url ? (
            <Box component="video" data-testid="onboarding-recording-video" src={recording.url} controls autoPlay preload="auto" sx={{ display: "block", width: "100%", borderRadius: 2, background: "#0B1220" }} />
          ) : recording?.error ? (
            <Alert severity="warning">{recording.error}</Alert>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 3, justifyContent: "center" }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 13, color: "#5B6583" }}>Fetching the recording...</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
